import { ReactElement, useEffect, useRef } from "react";
import { useSetSetting } from "../../utils/setting";
import { useAtom } from "jotai";
import { accessTokenInfoAtom, isGoogleSSOAtom } from "../../store";
import {
  CredentialResponseType,
  LoginErrorCode,
  LoginMethod,
} from "../../types";
import { useGetAccessToken } from "../../utils/token";
import { Box } from "@chakra-ui/react";

type GoogleLoginButtonProps = {
  onInvalidUserInfo: (errorCode: LoginErrorCode) => void;
};

export function GoogleLoginButton({
  onInvalidUserInfo,
}: Readonly<GoogleLoginButtonProps>): ReactElement {
  const buttonRef = useRef<HTMLDivElement>(null);
  const gClientId = import.meta.env.VITE_APP_GCLOUD_CLIENT_ID;

  const getAccessToken = useGetAccessToken();
  const [, setAccessTokenInfo] = useAtom(accessTokenInfoAtom);
  const setSettingInfo = useSetSetting();
  const [, setIsGoogleSSO] = useAtom(isGoogleSSOAtom);

  useEffect(() => {
    const handleCredentialResponse = async (
      credentialResponse: CredentialResponseType,
    ): Promise<void> => {
      const sendData = {
        credential: credentialResponse.credential,
      };
      const token = await getAccessToken(
        LoginMethod.GoogleAuth,
        onInvalidUserInfo,
        sendData,
      );
      if (!token) {
        return;
      }
      setAccessTokenInfo(token);
      setSettingInfo(token);
      setIsGoogleSSO(true);
    };

    if (buttonRef.current && window.google) {
      window.google.accounts.id.initialize({
        client_id: gClientId,
        callback: handleCredentialResponse,
        ux_mode: "popup",
        context: "signin",
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        text: "signin_with",
        type: "standard",
        size: "large",
        theme: "outline",
        logo_alignment: "left",
      });
    }
  }, [
    gClientId,
    getAccessToken,
    onInvalidUserInfo,
    setAccessTokenInfo,
    setIsGoogleSSO,
    setSettingInfo,
  ]);

  return (
    <Box>
      <div ref={buttonRef} />
    </Box>
  );
}
